import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    marginBottom: theme.spacing(1),
    flexWrap: "wrap",
  },
  search: {
    flex: 1,
    minWidth: 200,
  },
  list: {
    maxHeight: 420,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  preview: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    paddingRight: theme.spacing(6),
  },
  chip: {
    marginLeft: theme.spacing(1),
    height: 20,
    fontSize: 11,
  },
  empty: {
    padding: theme.spacing(3),
    textAlign: "center",
  },
}));

// Gestão de quais templates oficiais (já sincronizados) ficam disponíveis
// para os usuários desta conexão. Templates ocultos não aparecem no ticket,
// agendamento, troca de canal nem na criação de campanhas Meta.
const TemplatesVisibilityModal = ({ open, onClose, whatsApp }) => {
  const classes = useStyles();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open || !whatsApp?.id) return;

    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/whatsapp/${whatsApp.id}/templates`);
        setTemplates(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    };

    setSearch("");
    fetchTemplates();
  }, [open, whatsApp]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return templates;
    return templates.filter(
      (t) =>
        t.shortcode?.toLowerCase().includes(term) ||
        t.message?.toLowerCase().includes(term)
    );
  }, [templates, search]);

  const enabledCount = templates.filter((t) => t.enabled !== false).length;

  const updateVisibility = async (ids, enabled) => {
    if (ids.length === 0) return;
    setSaving(true);
    try {
      await api.put(`/whatsapp/${whatsApp.id}/templates/visibility`, {
        templateIds: ids,
        enabled,
      });
      const idSet = new Set(ids);
      setTemplates((prev) =>
        prev.map((t) => (idSet.has(t.id) ? { ...t, enabled } : t))
      );
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const handleBulk = (enabled) => {
    const ids = filtered
      .filter((t) => (t.enabled !== false) !== enabled)
      .map((t) => t.id);
    if (ids.length === 0) {
      toast.info("Nenhum template para alterar");
      return;
    }
    updateVisibility(ids, enabled);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        Templates disponíveis — {whatsApp?.name}
        <Typography variant="body2" color="textSecondary">
          {enabledCount} de {templates.length} visíveis para os usuários
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box className={classes.toolbar}>
          <TextField
            className={classes.search}
            size="small"
            variant="outlined"
            placeholder="Pesquisar templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            size="small"
            variant="outlined"
            disabled={saving || loading}
            onClick={() => handleBulk(true)}
          >
            Mostrar todos
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={saving || loading}
            onClick={() => handleBulk(false)}
          >
            Ocultar todos
          </Button>
        </Box>

        {loading ? (
          <Box className={classes.empty}>
            <CircularProgress size={28} />
          </Box>
        ) : templates.length === 0 ? (
          <Typography className={classes.empty} color="textSecondary">
            Nenhum template sincronizado. Use o botão "Sincronizar templates"
            primeiro.
          </Typography>
        ) : filtered.length === 0 ? (
          <Typography className={classes.empty} color="textSecondary">
            Nenhum template encontrado
          </Typography>
        ) : (
          <List dense className={classes.list}>
            {filtered.map((template) => {
              const enabled = template.enabled !== false;
              return (
                <ListItem key={template.id} divider>
                  <ListItemText
                    primary={
                      <>
                        {template.shortcode}
                        {template.category && (
                          <Chip
                            className={classes.chip}
                            size="small"
                            label={template.category}
                          />
                        )}
                        {template.language && (
                          <Chip
                            className={classes.chip}
                            size="small"
                            variant="outlined"
                            label={template.language}
                          />
                        )}
                      </>
                    }
                    secondary={
                      <span className={classes.preview}>
                        {template.message}
                      </span>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      edge="end"
                      color="primary"
                      checked={enabled}
                      disabled={saving}
                      onChange={() =>
                        updateVisibility([template.id], !enabled)
                      }
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplatesVisibilityModal;
